<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ElMessage, type UploadFile, type UploadRawFile, type UploadUserFile } from 'element-plus'
import { ref } from 'vue'

const fileList = defineModel<UploadUserFile[]>()

const props = withDefaults(
  defineProps<{
    maxSizeMb?: number
    acceptableMimeTypes?: string[]
    disabled?: boolean
    limit?: number
    fileUrls?: string[]
  }>(),
  {
    maxSizeMb: 10,
    acceptableMimeTypes: () => [],
    disabled: false,
    limit: 5,
    fileUrls: () => [],
  }
)

const uploadRef = ref()

const handleFileChange = async (file: UploadFile) => {
  // `v-model:file-list` already syncs added files into the model, so we only
  // need to remove invalid ones here (pushing again would duplicate the file).
  if (!validateFile(file.raw)) uploadRef.value.handleRemove(file)
}

const validateFile = (file?: UploadRawFile) => {
  if (!file) return false

  const isValidType = props.acceptableMimeTypes.length === 0 || props.acceptableMimeTypes.includes(file.type)
  const isValidSize = file.size < 1_048_576 * props.maxSizeMb

  if (!isValidType)
    ElMessage.error(`${file.name}: file must be one of the following types: ${props.acceptableMimeTypes.join(', ')}.`)
  if (!isValidSize) ElMessage.error(`${file.name}: file size must be smaller than or equal to ${props.maxSizeMb}MB.`)

  return isValidType && isValidSize
}

const handleFileExceed = () => {
  ElMessage.warning(`Maximum ${props.limit} files allowed`)
}
</script>

<template>
  <div class="upload-wrapper flex flex-col gap-spacing-sm">
    <el-upload
      ref="uploadRef"
      v-model:file-list="fileList"
      list-type="text"
      :auto-upload="false"
      :on-change="handleFileChange"
      :on-exceed="handleFileExceed"
      :limit="limit"
      :disabled="disabled"
      multiple
      drag
    >
      <div
        v-if="!fileList || fileList.length < limit"
        class="h-full flex flex-col items-center justify-center gap-spacing-2xs text-center p-8"
      >
        <Icon icon="material-symbols:upload-rounded" :width="48" :height="48" />
        <p><span style="color: var(--text-brand)">Upload files</span> or drag and drop</p>
        <small class="text-xs">Any format up to {{ props.maxSizeMb }}MB</small>
      </div>
      <p v-else class="p-2 text-xs text-left">Reached the {{ limit }} files limit</p>
    </el-upload>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-upload;

  // Avoid overflowing the whole drawer with long file names
  .el-upload-list__item-info {
    max-width: 18em;
  }

  .el-upload {
    border-radius: 8px;
  }
}
</style>
